/**
 * AG Grid State Persistence Utility
 * Saves and restores grid column state (order, sizing) to localStorage
 */

const STORAGE_PREFIX = 'RSLT-grid-';
const GRID_STATE_VERSION = 1;

function getStorageKey(gridKey: string): string {
	return `${STORAGE_PREFIX}${GRID_STATE_VERSION}-${gridKey}`;
}

interface GridStatePartial {
	columnOrder?: { orderedColIds: string[] };
	columnSizing?: { columnSizingModel: Array<{ colId: string; width?: number; flex?: number }> };
}

/**
 * Save grid state to localStorage
 * Only persists columnOrder and columnSizing - filters, sort, etc. are excluded
 * @param gridKey - Unique identifier, typically the component name (e.g., 'PaymentPlanIndex')
 * @param state - Full grid state from AG Grid's stateUpdated event
 */
export function saveGridState(gridKey: string, state: any): void {
	const persistableState: GridStatePartial = {};

	if (state.columnOrder) {
		persistableState.columnOrder = state.columnOrder;
	}
	if (state.columnSizing) {
		persistableState.columnSizing = state.columnSizing;
	}

	if (Object.keys(persistableState).length > 0) {
		localStorage.setItem(getStorageKey(gridKey), JSON.stringify(persistableState));
	}
}

/**
 * Load grid state from localStorage
 * @param gridKey - Unique identifier matching the one used in saveGridState
 * @returns Partial grid state or null if not found
 */
export function loadGridState(gridKey: string): GridStatePartial | null {
	try {
		const stored = localStorage.getItem(getStorageKey(gridKey));
		if (!stored) return null;

		const state = JSON.parse(stored);
		// Return with partialColumnState flag so AG Grid applies only these properties
		return {
			...state,
			partialColumnState: true
		};
	} catch {
		return null;
	}
}

/**
 * Clear saved grid state
 * @param gridKey - Unique identifier for the grid
 */
export function clearGridState(gridKey: string): void {
	localStorage.removeItem(getStorageKey(gridKey));
}

/**
 * Apply saved state to column definitions before grid renders
 * Prevents visual column reordering after grid mount
 */
export function applyStateToColumnDefs(columnDefs: any[], savedState: GridStatePartial | null): any[] {
	if (!savedState) return columnDefs;

	let result = [...columnDefs];

	// Apply column order
	if (savedState.columnOrder?.orderedColIds) {
		const orderMap = new Map<string, number>();
		savedState.columnOrder.orderedColIds.forEach((colId, index) => {
			orderMap.set(colId, index);
		});

		result.sort((a, b) => {
			const aId = a.colId ?? a.field;
			const bId = b.colId ?? b.field;
			const aIndex = orderMap.get(aId) ?? 999;
			const bIndex = orderMap.get(bId) ?? 999;
			return aIndex - bIndex;
		});
	}

	// Apply column widths
	if (savedState.columnSizing?.columnSizingModel) {
		const sizingMap = new Map<string, { width?: number; flex?: number }>();
		savedState.columnSizing.columnSizingModel.forEach((sizing) => {
			sizingMap.set(sizing.colId, sizing);
		});

		result = result.map((col) => {
			const sizing = sizingMap.get(col.colId ?? col.field);
			if (sizing) {
				return {
					...col,
					width: sizing.width ?? col.width,
					flex: sizing.flex
				};
			}
			return col;
		});
	}

	return result;
}
